import { useState } from 'react'
import { api, useAbruf, type ApiFehler, type Seite } from '../api'
import { Fehleranzeige, Karte, Kennzahl, Merkmal, Tabelle, Zustand } from '../bausteine'
import { datum, zeitpunkt } from '../formate'

interface Nachrichtenzeile {
  id: string
  richtung: string
  status: string
  nachrichtentyp: string
  formatversion: string | null
  pruefidentifikator: string | null
  absender: string | null
  empfaenger: string | null
  datenaustauschreferenz: string
  eingegangenAm: string | null
  versandtAm: string | null
  testdatenaustausch: boolean
}

interface Klaerfallzeile {
  id: string
  kategorie: string
  status: string
  betreff: string
  beschreibung: string | null
  zugewiesenAn: string | null
  faelligAm: string | null
}

interface Fristzeile {
  id: string
  art: string
  bezeichnung: string | null
  ausgangstag: string | null
  werktage: number | null
  faelligAm: string
  fundstelle: string | null
}

interface Uebersicht {
  offeneKlaerfaelle: number
  offeneFristen: number
  abgelaufeneFristen: number
}

/**
 * Die Zustaende sind richtungsgebunden: eine eingegangene Nachricht kann nicht
 * "versandt" sein. Nur ZU_KLAEREN gibt es in beiden Richtungen.
 */
// Aufzaehlung: Nachrichtenstatus
const zustaende: Record<string, string[]> = {
  EINGANG: ['EMPFANGEN', 'GEPRUEFT', 'ZURUECKGEWIESEN', 'VERARBEITET', 'ZU_KLAEREN'],
  AUSGANG: [
    'ENTWURF',
    'ZUM_VERSAND',
    'VERSANDT',
    'ZUGESTELLT',
    'ZUSTELLUNG_GESCHEITERT',
    'ZU_KLAEREN',
  ],
}

export default function Marktkommunikation() {
  const uebersicht = useAbruf<Uebersicht>('/api/mako/uebersicht')
  const [richtung, setzeRichtung] = useState('EINGANG')
  const [status, setzeStatus] = useState('')
  const nachrichten = useAbruf<Seite<Nachrichtenzeile>>(
    `/api/mako/postfach?richtung=${richtung}&groesse=25` +
      (status ? `&status=${status}` : ''),
  )
  const klaerfaelle = useAbruf<Seite<Klaerfallzeile>>('/api/mako/klaerfaelle?status=OFFEN')
  const fristen = useAbruf<Fristzeile[]>('/api/mako/fristen')

  const [fehler, setzeFehler] = useState<ApiFehler>()
  const [meldung, setzeMeldung] = useState<string>()

  async function erledigen(klaerfallId: string) {
    setzeFehler(undefined)
    try {
      await api.sende(`/api/mako/klaerfaelle/${klaerfallId}/erledigung`, {
        loesung: 'Von Hand erledigt im Backoffice.',
      })
      setzeMeldung('Der Klärfall wurde erledigt.')
      klaerfaelle.erneut()
      uebersicht.erneut()
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler)
    }
  }

  return (
    <>
      <h1>Marktkommunikation</h1>
      <p className="untertitel">
        Der Rahmen steht: Nachrichten werden gegen das Regelwerk geprüft, Fristen laufen,
        Klärfälle entstehen. Die Geschäftsprozesse selbst sind bewusst noch nicht
        umgesetzt.
      </p>

      <div className="karten">
        <Kennzahl
          beschriftung="Offene Klärfälle"
          wert={uebersicht.daten?.offeneKlaerfaelle ?? '…'}
        />
        <Kennzahl beschriftung="Laufende Fristen" wert={uebersicht.daten?.offeneFristen ?? '…'} />
        <Kennzahl
          beschriftung="Abgelaufene Fristen"
          wert={uebersicht.daten?.abgelaufeneFristen ?? '…'}
        />
      </div>

      <Karte titel="Nachrichtenpostfach">
        <div className="leiste">
          <div className="feld">
            <label htmlFor="richtung">Richtung</label>
            <select
              id="richtung"
              value={richtung}
              onChange={(ereignis) => {
                const neu = ereignis.target.value
                setzeRichtung(neu)
                setzeStatus('')
              }}
            >
              <option value="EINGANG">Eingang</option>
              <option value="AUSGANG">Ausgang</option>
            </select>
          </div>
          <div className="feld">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              value={status}
              onChange={(ereignis) => setzeStatus(ereignis.target.value)}
            >
              <option value="">Alle</option>
              {zustaende[richtung].map((wert) => (
                <option key={wert} value={wert}>
                  {wert}
                </option>
              ))}
            </select>
          </div>
        </div>
        <Zustand
          laeuft={nachrichten.laeuft}
          fehler={nachrichten.fehler}
          leer={(nachrichten.daten?.inhalt ?? []).length === 0}
          leertext="Keine Nachricht in diesem Zustand."
        >
          <Tabelle
            spalten={[
              { name: 'Typ' },
              { name: 'Status' },
              { name: 'Prüfidentifikator' },
              { name: 'Format' },
              { name: 'Absender' },
              { name: 'Empfänger' },
              { name: 'Zeitpunkt' },
            ]}
          >
            {(nachrichten.daten?.inhalt ?? []).map((nachricht) => (
              <tr key={nachricht.id}>
                <td>
                  {nachricht.nachrichtentyp}
                  {nachricht.testdatenaustausch && (
                    <>
                      {' '}
                      <Merkmal art="warnung">Test</Merkmal>
                    </>
                  )}
                </td>
                <td>
                  <Merkmal
                    art={
                      nachricht.status === 'ZU_KLAEREN'
                        ? 'warnung'
                        : nachricht.status === 'ZURUECKGEWIESEN' ||
                            nachricht.status === 'ZUSTELLUNG_GESCHEITERT'
                          ? 'fehler'
                          : 'neutral'
                    }
                  >
                    {nachricht.status}
                  </Merkmal>
                </td>
                <td>{nachricht.pruefidentifikator ?? '—'}</td>
                <td>{nachricht.formatversion ?? '—'}</td>
                <td>{nachricht.absender ?? '—'}</td>
                <td>{nachricht.empfaenger ?? '—'}</td>
                <td>{zeitpunkt(nachricht.eingegangenAm ?? nachricht.versandtAm)}</td>
              </tr>
            ))}
          </Tabelle>
        </Zustand>
      </Karte>

      <Karte titel="Offene Klärfälle">
        {meldung && <div className="hinweis info">{meldung}</div>}
        {fehler && <Fehleranzeige fehler={fehler} />}
        <Zustand
          laeuft={klaerfaelle.laeuft}
          fehler={klaerfaelle.fehler}
          leer={(klaerfaelle.daten?.inhalt ?? []).length === 0}
          leertext="Keine offenen Klärfälle."
        >
          <Tabelle
            spalten={[
              { name: 'Kategorie' },
              { name: 'Betreff' },
              { name: 'Fällig am' },
              { name: 'Zugewiesen' },
              { name: '' },
            ]}
          >
            {(klaerfaelle.daten?.inhalt ?? []).map((klaerfall) => (
              <tr key={klaerfall.id}>
                <td>
                  <Merkmal art="warnung">{klaerfall.kategorie}</Merkmal>
                </td>
                <td>
                  {klaerfall.betreff}
                  {klaerfall.beschreibung && (
                    <div style={{ fontSize: 12, color: 'var(--gedaempft)' }}>
                      {klaerfall.beschreibung}
                    </div>
                  )}
                </td>
                <td>{datum(klaerfall.faelligAm)}</td>
                <td>{klaerfall.zugewiesenAn ?? '—'}</td>
                <td>
                  <button onClick={() => erledigen(klaerfall.id)}>Erledigen</button>
                </td>
              </tr>
            ))}
          </Tabelle>
        </Zustand>
      </Karte>

      <Karte titel="Laufende Fristen">
        <Zustand
          laeuft={fristen.laeuft}
          fehler={fristen.fehler}
          leer={(fristen.daten ?? []).length === 0}
          leertext="Keine Frist in den nächsten zwei Wochen."
        >
          <Tabelle
            spalten={[
              { name: 'Art' },
              { name: 'Bezeichnung' },
              { name: 'Ausgangstag' },
              { name: 'Werktage', zahl: true },
              { name: 'Fällig am' },
              { name: 'Fundstelle' },
            ]}
          >
            {(fristen.daten ?? []).map((frist) => (
              <tr key={frist.id}>
                <td>{frist.art}</td>
                <td>{frist.bezeichnung ?? '—'}</td>
                <td>{datum(frist.ausgangstag)}</td>
                <td className="zahl">{frist.werktage ?? '—'}</td>
                <td>{datum(frist.faelligAm)}</td>
                <td style={{ fontSize: 12, color: 'var(--gedaempft)' }}>
                  {frist.fundstelle ?? '—'}
                </td>
              </tr>
            ))}
          </Tabelle>
        </Zustand>
      </Karte>
    </>
  )
}
